import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface PendingDualRoleRequest {
  id: string;
  user_id: string;
  requested_role: "mentor" | "entrepreneur";
  requested_at: string;
  status: "pending" | "approved" | "declined";
  profile: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
    role: string | null;
  } | null;
}

export function usePendingDualRoleRequests() {
  return useQuery({
    queryKey: ["dual_role_requests_admin", "pending"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("dual_role_requests")
        .select("id, user_id, requested_role, requested_at, status")
        .eq("status", "pending")
        .order("requested_at", { ascending: true });
      if (error) throw error;

      const userIds = (data || []).map((r) => r.user_id);
      if (userIds.length === 0) return [] as PendingDualRoleRequest[];

      const { data: profiles, error: pErr } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, role")
        .in("id", userIds);
      if (pErr) throw pErr;

      const profileMap = new Map((profiles || []).map((p: any) => [p.id, p]));

      return (data || []).map((r: any) => ({
        ...r,
        profile: profileMap.get(r.user_id) ?? null,
      })) as PendingDualRoleRequest[];
    },
    staleTime: 60 * 1000,
  });
}

export function useApproveDualRoleRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase.rpc("approve_dual_role_request", {
        p_request_id: requestId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dual_role_requests_admin"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Role request approved");
    },
    onError: (error: Error) => {
      toast.error("Failed to approve: " + error.message);
    },
  });
}

export function useDeclineDualRoleRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, reason }: { requestId: string; reason?: string }) => {
      const { error } = await supabase.rpc("decline_dual_role_request", {
        p_request_id: requestId,
        p_reason: reason ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dual_role_requests_admin"] });
      toast.success("Role request declined");
    },
    onError: (error: Error) => {
      toast.error("Failed to decline: " + error.message);
    },
  });
}

export const useGrantDualRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, roleToGrant }: { userId: string; roleToGrant: 'mentor' | 'entrepreneur' }) => {
      const { error } = await supabase.rpc('grant_dual_role', {
        p_user_id: userId,
        p_role_to_grant: roleToGrant,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin_profiles'] });
      queryClient.invalidateQueries({ queryKey: ['profile_review'] });
      queryClient.invalidateQueries({ queryKey: ['profile_review_full'] });
      toast.success('Role granted successfully');
    },
    onError: (error: Error) => {
      toast.error('Failed to grant role: ' + error.message);
    },
  });
};

export function useCancelDualRoleRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase
        .from("dual_role_requests")
        .delete()
        .eq("id", requestId)
        .eq("status", "pending");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dual_role_requests_admin"] });
      queryClient.invalidateQueries({ queryKey: ["dual_role_requests_user"] });
      toast.success("Role request withdrawn");
    },
    onError: (error: Error) => {
      toast.error("Failed to withdraw request: " + error.message);
    },
  });
}
